
import { useImperativeHandle } from 'react';
import { MexicoMapRef } from './types';

interface IMexicoMapHandlesProps {
  map: React.MutableRefObject<any>;
  isMobile: boolean;
  setMapZoomLevel: (lvl: 'country' | 'region' | 'city') => void;
  setShowChiapasCities: (v: boolean) => void;
  setShowHidalgoCities: (v: boolean) => void;
  toastShownRef: React.MutableRefObject<{ chiapas: boolean; hidalgo: boolean }>;
  formatCurrencyShort: (n: number) => string;
  ref: React.Ref<any>;
  selectedRegion: any;
  setShowGlobalRegions: (v: boolean) => void;
  showMexicoView?: () => void;
  setIsMexicoView?: (v: boolean) => void;
}

/**
 * Expose imperative map actions through the ref.
 */
export function useMexicoMapHandles({
  map,
  isMobile,
  setMapZoomLevel,
  setShowChiapasCities,
  setShowHidalgoCities,
  toastShownRef,
  formatCurrencyShort,
  ref,
  selectedRegion,
  setShowGlobalRegions,
  showMexicoView,
  setIsMexicoView
}: IMexicoMapHandlesProps) {
  useImperativeHandle(ref, () => ({
    flyToRegion: (region) => {
      if (map.current) {
        console.log('flyToRegion called for:', region.english_name);
        console.log('Region pledges:', formatCurrencyShort(region.pledges));
        map.current.easeTo({ 
          center: region.coordinates,
          zoom: isMobile ? 5.5 : 7,
          pitch: 30,
          bearing: 0,
          duration: 1500,
          essential: true 
        });
        setMapZoomLevel('region');
        setShowGlobalRegions(false); // Ensure global view is turned off
        if (setIsMexicoView) setIsMexicoView(false);
        
        if (region.region_id === 'CHIS') {
          setShowChiapasCities(true);
          setShowHidalgoCities(false);
          toastShownRef.current.chiapas = true;
        } else if (region.region_id === 'HGO') {
          setShowHidalgoCities(true);
          setShowChiapasCities(false);
          toastShownRef.current.hidalgo = true;
        } else {
          setShowChiapasCities(false);
          setShowHidalgoCities(false);
        }
      }
    },
    flyToCity: (city) => {
      if (map.current) {
        console.log('flyToCity called for:', city.name);
        map.current.easeTo({ 
          center: city.coordinates,
          zoom: isMobile ? 10 : 11,
          pitch: 45,
          bearing: 15,
          duration: 1500,
          essential: true 
        });
        setMapZoomLevel('city');
        setShowGlobalRegions(false); // Ensure global view is turned off
        if (setIsMexicoView) setIsMexicoView(false);
      }
    },
    resetView: () => {
      if (map.current) {
        console.log('resetView called, showing global view');
        map.current.easeTo({ 
          center: [0, 20], // World center
          zoom: isMobile ? 1.8 : 2.2,
          pitch: 0,
          bearing: 0,
          duration: 1500,
          essential: true 
        });
        setMapZoomLevel('country');
        setShowChiapasCities(false);
        setShowHidalgoCities(false);
        setShowGlobalRegions(true); // Ensure global view is enabled
        if (setIsMexicoView) setIsMexicoView(false);
        toastShownRef.current = { chiapas: false, hidalgo: false };
      }
    },
    showMexicoView: () => {
      if (showMexicoView) {
        // Use the provided function if available
        showMexicoView();
        if (setIsMexicoView) setIsMexicoView(true);
      } else if (map.current) {
        // Fallback to the original implementation
        console.log('showMexicoView called, focusing on Mexico');
        map.current.easeTo({ 
          // Further adjusted center coordinates for better mobile viewing
          center: isMobile ? [-101, 23] : [-102.5, 23.6], 
          // Reduced zoom on mobile to see more of Mexico
          zoom: isMobile ? 2.4 : 4.5,
          pitch: 0, 
          bearing: 0,
          duration: 1500,
          essential: true 
        });
        setMapZoomLevel('country');
        setShowChiapasCities(false);
        setShowHidalgoCities(false);
        setShowGlobalRegions(false); // Disable global view to show Mexico
        if (setIsMexicoView) setIsMexicoView(true);
        toastShownRef.current = { chiapas: false, hidalgo: false };
      }
    },
    focusRegion: (regionId: string) => {
      // Keep existing code for focusRegion
    }
  }));
}
